import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import PaidIcon from '@mui/icons-material/Paid';
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { useTheme } from '@mui/material/styles';
import { db, collection, getDocs, doc, setDoc, getDoc, onSnapshot } from '../firebaseConfig';
import { usePeriod } from './PeriodContext';
import InfoIcon from '@mui/icons-material/Info';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

Date.prototype.getWeek = function () {
  const date = new Date(this.getTime());
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() + 4 - (date.getDay() || 7));
  const yearStart = new Date(date.getFullYear(), 0, 1);
  const weekNumber = Math.ceil(((date - yearStart) / 86400000 + 1) / 7);
  return weekNumber;
};

const WinLossStats = ({ userID, capital = 0, trades }) => {
  const { selectedPeriod, selectedYear, selectedMonth, showMissedTrades, selectedTag } = usePeriod();
  const [journalEntries, setJournalEntries] = useState({});
  const [showStreak, setShowStreak] = useState(true); // Default to showing streak
  const theme = useTheme();
  const iconColor = theme.palette.mode === 'dark' ? theme.palette.grey[200] : theme.palette.grey[600];

  useEffect(() => {
    const fetchPreferences = () => {
      const settingsRef = doc(db, 'Users', userID, 'preferences', 'settings');
      const unsubscribe = onSnapshot(settingsRef, (settingsSnap) => {
        try {
          if (settingsSnap.exists()) {
            const settings = settingsSnap.data();
            const streakValue = settings.showStreak !== false; // Default to true if not set
            setShowStreak(streakValue);
            console.log("Fetched streak visibility preference:", streakValue);
          } else {
            setDoc(settingsRef, { showStreak: true }, { merge: true })
              .then(() => {
                setShowStreak(true);
                console.log("Created default streak visibility preference: true");
              })
              .catch((error) => {
                console.error("Error creating default preference:", error);
                setShowStreak(true);
              });
          }
        } catch (error) {
          console.error("Error in onSnapshot:", error);
          setShowStreak(true);
        }
      }, (error) => {
        console.error("Snapshot listener error:", error);
        setShowStreak(true);
      });
      return unsubscribe;
    };

    const fetchCapitalTransactions = async () => {
      try {
        const capitalRef = collection(db, 'Users', userID, 'capital');
        const capitalSnapshot = await getDocs(capitalRef);
        
        const transactions = capitalSnapshot.docs.map(doc => ({
          ...doc.data(),
          id: doc.id,
          date: new Date(doc.data().date)
        })).sort((a, b) => a.date - b.date);

        console.log("Raw capital transactions:", transactions);
        return transactions;
      } catch (error) {
        console.error("Error fetching capital transactions:", error);
        return [];
      }
    };

    const processData = async () => {
      await fetchPreferences(); // Fetch preferences first
      const capitalTransactions = await fetchCapitalTransactions();
      filterTrades(trades, capitalTransactions);
    };

    const calculateInitialCapital = (capitalTransactions) => {
      let total = capital;
      console.log("Starting calculation of total deposits/withdrawals with prop capital:", capital);

      capitalTransactions.forEach(trans => {
        if (trans.deposit) {
          total += Number(trans.deposit);
          console.log(`Added deposit: ${trans.deposit}, new total: ${total}`);
        }
        if (trans.deposits) {
          total += Number(trans.deposits);
          console.log(`Added legacy deposit: ${trans.deposits}, new total: ${total}`);
        }
        if (trans.withdrawal) {
          total -= Number(trans.withdrawal);
          console.log(`Subtracted withdrawal: ${trans.withdrawal}, new total: ${total}`);
        }
        if (trans.withdrawals) {
          total -= Number(trans.withdrawals);
          console.log(`Subtracted legacy withdrawal: ${trans.withdrawals}, new total: ${total}`);
        }
      });

      console.log("Final capital from all deposits/withdrawals:", total);
      return total;
    };

    const filterTrades = (entries, capitalTransactions) => {
      let totalWins = 0;
      let totalLosses = 0;
      let winCount = 0;
      let lossCount = 0;
      let bestTradeWindow = 'N/A';
      let streakDots = [];
      let dailyReturns = {};
      let maxDrawdown = 0;
      let maxDrawdownAmount = 0;
      let relativeMaxDrawdown = 0;
      let relativeMaxDrawdownAmount = 0;
      let mostProfitableDay = 0;

      const periodStart = getPeriodStartDate();
      console.log("Period start date:", periodStart);
      
      let actualStartingCapital = calculateInitialCapital(capitalTransactions);
      console.log("Actual starting capital for period:", actualStartingCapital);

      entries
        .filter(entry => entry.status === (showMissedTrades ? 'Missed' : 'Closed'))
        .sort((a, b) => new Date(a.date) - new Date(b.date))
        .forEach((entry) => {
          const entryDate = new Date(entry.date);
          const matchesTag = selectedTag === 'all' || 
            (Array.isArray(entry.tags) && entry.tags.includes(selectedTag));

          if (matchesPeriod(entryDate) && matchesTag) {
            const returns = parseFloat(entry.returns);
            const dateKey = entryDate.toDateString();

            if (!dailyReturns[dateKey]) {
              dailyReturns[dateKey] = 0;
            }
            dailyReturns[dateKey] += returns;

            if (returns > 0) {
              totalWins += returns;
              winCount++;
            } else if (returns < 0) {
              totalLosses += Math.abs(returns);
              lossCount++;
            }
          }
        });

      let peak = actualStartingCapital;
      let runningTotal = 0;
      let currentCapital = actualStartingCapital;

      Object.keys(dailyReturns)
        .sort((a, b) => new Date(a) - new Date(b))
        .forEach(date => {
          const dailyReturn = dailyReturns[date];
          runningTotal += dailyReturn;
          currentCapital = actualStartingCapital + runningTotal;

          if (dailyReturn > mostProfitableDay) {
            mostProfitableDay = dailyReturn;
          }

          if (currentCapital > peak && currentCapital > 0) {
            peak = currentCapital;
          }

          if (currentCapital > 0 && peak > 0) {
            const drawdownAmount = peak - currentCapital;
            const drawdownPercent = Math.min((drawdownAmount / peak) * 100, 100);

            if (drawdownAmount > 0) {
              if (drawdownPercent > maxDrawdown) {
                maxDrawdown = drawdownPercent;
                maxDrawdownAmount = drawdownAmount;
              }

              const relativeDrawdownPercent = Math.min((drawdownAmount / currentCapital) * 100, 100);
              if (relativeDrawdownPercent > relativeMaxDrawdown) {
                relativeMaxDrawdown = relativeDrawdownPercent;
                relativeMaxDrawdownAmount = drawdownAmount;
              }
            }
          }
        });

      Object.values(dailyReturns).forEach(dailyReturn => {
        streakDots.push(dailyReturn > 0 ? 'green' : dailyReturn < 0 ? 'red' : 'neutral');
      });

      const sessions = calculateSessionPerformance(entries);
      bestTradeWindow = determineBestSession(sessions);
      const currentStreakCount = calculateCurrentStreak(dailyReturns);

      const averageWin = winCount > 0 ? (totalWins / winCount).toFixed(2) : 0;
      const averageLoss = lossCount > 0 ? (totalLosses / lossCount).toFixed(2) : 0;
      const winLossRatio = lossCount > 0 ? (winCount / lossCount).toFixed(2) : '∞';
      const consistencyPercent = totalWins > 0 ? (100 - (mostProfitableDay / totalWins) * 100).toFixed(2) : 0;

      setJournalEntries({
        totalWins,
        totalLosses,
        winCount,
        lossCount,
        averageWin,
        averageLoss,
        winLossRatio,
        bestTradeWindow,
        currentStreak: streakDots.slice(-10),
        streakDots,
        currentStreakCount,
        maxDrawdown: maxDrawdown.toFixed(2),
        maxDrawdownAmount,
        relativeMaxDrawdown: relativeMaxDrawdown.toFixed(2),
        relativeMaxDrawdownAmount,
        peak: peak.toFixed(2),
        currentCapital: currentCapital.toFixed(2),
        consistencyPercent: Math.max(consistencyPercent, 0)
      });
    };

    processData();
  }, [userID, selectedPeriod, selectedYear, selectedMonth, showMissedTrades, selectedTag, capital, trades]);

  const handleToggleStreak = async () => {
    const newShowStreak = !showStreak;
    setShowStreak(newShowStreak);
    
    try {
      const settingsRef = doc(db, 'Users', userID, 'preferences', 'settings');
      await setDoc(settingsRef, { showStreak: newShowStreak }, { merge: true });
      console.log("Streak visibility preference saved:", newShowStreak);
      // Add a slight delay to ensure Firestore syncs
      await new Promise(resolve => setTimeout(resolve, 500));
    } catch (error) {
      console.error("Error saving streak visibility preference:", error);
      // Revert state on failure to keep UI consistent
      setShowStreak(!newShowStreak);
    }
  };

  const getPeriodStartDate = () => {
    const now = new Date();
    switch (selectedPeriod) {
      case 'today':
        return new Date(now.setHours(0, 0, 0, 0));
      case 'this-week':
        return new Date(now.setDate(now.getDate() - now.getDay()));
      case 'this-month':
        return new Date(now.getFullYear(), now.getMonth(), 1);
      case 'this-year':
        return new Date(now.getFullYear(), 0, 1);
      case 'pick-month':
        return new Date(selectedYear, selectedMonth - 1, 1);
      case 'pick-year':
        return new Date(selectedYear, 0, 1);
      case 'pick-month-year':
        return new Date(selectedYear, selectedMonth - 1, 1);
      case 'all-time':
        return new Date(1970, 0, 1);
      default:
        return new Date(1970, 0, 1);
    }
  };

  const matchesPeriod = (date) => {
    const periodStart = getPeriodStartDate();
    if (selectedPeriod === 'this-week') {
      return date.getWeek() === new Date().getWeek() && date.getFullYear() === new Date().getFullYear();
    }
    return date >= periodStart;
  };

  const calculateSessionPerformance = (entries) => {
    const sessions = {
      'New York': { wins: 0, total: 0 },
      'London': { wins: 0, total: 0 },
      'Asia': { wins: 0, total: 0 }
    };

    entries.forEach(entry => {
      const matchesTag = selectedTag === 'all' || 
        (Array.isArray(entry.tags) && entry.tags.includes(selectedTag));
      if (matchesPeriod(new Date(entry.date)) && matchesTag) {
        const session = categorizeSession(entry.date, entry.entryTime);
        sessions[session].total++;
        if (parseFloat(entry.returns) > 0) {
          sessions[session].wins++;
        }
      }
    });

    return sessions;
  };

  const categorizeSession = (date, time) => {
    const hour = new Date(`${date} ${time}`).getUTCHours();
    if (hour >= 22 || hour < 7) return 'Asia';
    if (hour >= 7 && hour < 16) return 'London';
    if (hour >= 12 && hour < 21) return 'New York';
    return 'Asia';
  };

  const determineBestSession = (sessions) => {
    const minTrades = 2;
    let bestSession = 'N/A';
    let bestWinRate = 0;

    Object.entries(sessions).forEach(([session, stats]) => {
      if (stats.total >= minTrades) {
        const winRate = stats.wins / stats.total;
        if (winRate > bestWinRate) {
          bestWinRate = winRate;
          bestSession = session;
        }
      }
    });

    return bestSession;
  };

  const calculateCurrentStreak = (dailyReturns) => {
    const dates = Object.keys(dailyReturns).sort((a, b) => new Date(b) - new Date(a));
    let streak = 0;
    
    for (const date of dates) {
      if (dailyReturns[date] > 0) {
        streak++;
      } else {
        break;
      }
    }
    
    return streak;
  };

  const {
    totalWins = 0,
    totalLosses = 0,
    winCount = 0,
    lossCount = 0,
    averageWin = 0,
    averageLoss = 0,
    peak = 0,
    winLossRatio = '∞',
    maxDrawdown = 0,
    maxDrawdownAmount = 0,
    relativeMaxDrawdown = 0,
    relativeMaxDrawdownAmount = 0,
    bestTradeWindow = 'N/A',
    currentStreak = [],
    currentStreakCount = 0,
    consistencyPercent = 0
  } = journalEntries;

  const renderStreakDots = () => {
    return (
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <div style={{ marginRight: '8px' }}>
          {currentStreak.map((dot, index) => (
            <span key={index} style={{ fontSize: '.9rem', color: dot === 'green' ? 'green' : dot === 'red' ? 'red' : 'grey' }}>●</span>
          ))}
        </div>
        <MDTypography variant="button" fontWeight="normal" color="text" fontFamily="inherit">
          {currentStreakCount} Days
        </MDTypography>
      </div>
    );
  };

  const tooltipContent = `The Trading Performance Metrics track key stats for ${showMissedTrades ? 'missed' : 'closed'} trades:\n- Win/Loss Ratio: Wins divided by losses\n- Average Win/Loss: Average profit/loss per trade\n- Max Drawdown: Largest peak-to-trough drop\n- Best Trade Window: Most profitable session\n- Streak: Consecutive profitable days\n- Consistency %: Measures profit distribution (100연구
 - (Most Profitable Day / Total Profits) * 100)\n  - Green: ≥70% (consistent)\n  - Yellow: 30%-70% (moderate)\n  - Red: <30% (inconsistent)`;

  return (
    <Card style={{ minHeight: '270px' }}>
      <MDBox display="flex" flexDirection="column" justifyContent="space-between" pt={1} px={2} height="100%">
        <MDBox display="flex" justifyContent="space-between">
          <MDBox
            variant="gradient"
            bgColor="light"
            color="dark"
            coloredShadow="light"
            borderRadius="xl"
            display="flex"
            justifyContent="center"
            alignItems="center"
            width="4rem"
            height="4rem"
            mt={-3}
          >
            <PaidIcon fontSize="medium" color="inherit" />
          </MDBox>
          <MDBox textAlign="right" lineHeight={1.25}>
            <MDTypography variant="button" fontWeight="light" color="text" fontFamily="inherit">
              Win/Loss Stats {showMissedTrades ? '(Missed)' : ''}
            </MDTypography>
            <Tooltip 
              title={tooltipContent}
              placement="bottom-end"
              componentsProps={{
                tooltip: {
                  sx: {
                    bgcolor: theme.palette.background.paper,
                    color: theme.palette.text.primary,
                    border: `1px solid ${theme.palette.divider}`,
                    p: 1.5,
                    maxWidth: '250px',
                    fontSize: '0.75rem',
                    lineHeight: 1.5,
                    '& .MuiTooltip-arrow': {
                      color: theme.palette.background.paper,
                    }
                  }
                }
              }}
            >
              <InfoIcon 
                sx={{ 
                  ml: 1, 
                  fontSize: '1rem',
                  color: iconColor,
                  cursor: 'help'
                }} 
              />
            </Tooltip>
            <MDTypography variant="h4" fontFamily="inherit" style={{ visibility: 'hidden' }}>0</MDTypography>
          </MDBox>
        </MDBox>
        <Divider />
        <MDBox pb={2} px={2}>
          <MDTypography component="p" variant="button" color="text" display="flex" fontFamily="inherit">
            Average Win:  <MDTypography component="span" variant="button" fontWeight="bold" color="success" fontFamily="inherit">${averageWin}</MDTypography>
          </MDTypography>
          <MDTypography component="p" variant="button" color="text" display="flex" fontFamily="inherit">
            Average Loss:  <MDTypography component="span" variant="button" fontWeight="bold" color="error" fontFamily="inherit">${averageLoss}</MDTypography>
          </MDTypography>
          <MDTypography component="p" variant="button" color="text" display="flex" fontFamily="inherit">
            Win/Loss Ratio:  <MDTypography component="span" variant="button" fontWeight="bold" color={winLossRatio > 1 ? "success" : "error"} fontFamily="inherit">{winLossRatio}</MDTypography>
          </MDTypography>
          <MDTypography component="p" variant="button" color="text" display="flex" fontFamily="inherit">
            Max Drawdown:  <MDTypography component="span" variant="button" fontWeight="bold" color="error" fontFamily="inherit">{maxDrawdown}% (${maxDrawdownAmount.toFixed(2)})</MDTypography>
          </MDTypography>
          <MDTypography component="p" variant="button" color="text" display="flex" fontFamily="inherit">
            Consistency %:  <MDTypography 
              component="span" 
              variant="button" 
              fontWeight="bold" 
              color={consistencyPercent >= 70 ? "success" : consistencyPercent >= 30 ? "warning" : "error"} 
              fontFamily="inherit"
            >
              {consistencyPercent}%
            </MDTypography>
          </MDTypography>
          <MDTypography component="p" variant="button" color="text" display="flex" fontFamily="inherit">
            Best Trade Window:  <MDTypography component="span" variant="button" fontWeight="bold" color="info" fontFamily="inherit">{bestTradeWindow}</MDTypography>
          </MDTypography>
          <MDTypography component="p" variant="button" color="text" display="flex" alignItems="center" fontFamily="inherit">
    Day Streak: 
    {showStreak && renderStreakDots()}
    <IconButton
      onClick={handleToggleStreak}
      size="small"
      sx={{ ml: 1, color: iconColor }}
      aria-label={showStreak ? "Hide streak" : "Show streak"}
    >
      {showStreak ? <VisibilityIcon fontSize="inherit" /> : <VisibilityOffIcon fontSize="inherit" />}
    </IconButton>
  </MDTypography>
</MDBox>
      </MDBox>
    </Card>
  );
};

WinLossStats.propTypes = {
  userID: PropTypes.string.isRequired,
  capital: PropTypes.number,
  trades: PropTypes.array.isRequired,
};

export default WinLossStats;